import { Button, Card, CardContent } from '@tark/shared/components';
import { useAuth, useTenantBootstrap } from '@tark/shared/hooks';
import {
  closeScheduleTimePoll,
  createScheduleTimePoll,
  getScheduleTimePoll,
  getScheduleTimePollOptions,
  getScheduleTimePolls,
  generateScheduleDraft,
  openScheduleTimePoll,
  updateScheduleTimePoll,
} from '@tark/shared/lib';
import type { TenantTimePoll, TenantTimePollBuilderOptions, TenantTimePollSavePayload } from '@tark/shared/types';
import { ArrowLeft, CalendarClock, Loader2, Plus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CoachPageHeader from '../components/CoachPageHeader';
import CoachTimePollBuilder from '../components/time-polls/CoachTimePollBuilder';
import CoachTimePollDetail from '../components/time-polls/CoachTimePollDetail';
import CoachTimePollList from '../components/time-polls/CoachTimePollList';

type View = 'list' | 'builder' | 'detail';

export default function CoachTimePollsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { activeTenant, isLoading: tenantLoading } = useTenantBootstrap(Boolean(user));
  const enabled = activeTenant?.config?.coachTimePollsEnabled === true;
  const scheduleDraftsEnabled = enabled && activeTenant?.config?.coachScheduleDraftsEnabled === true;
  const [view, setView] = useState<View>('list');
  const [polls, setPolls] = useState<TenantTimePoll[]>([]);
  const [options, setOptions] = useState<TenantTimePollBuilderOptions | null>(null);
  const [selected, setSelected] = useState<TenantTimePoll | null>(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const [nextPolls, nextOptions] = await Promise.all([getScheduleTimePolls(), getScheduleTimePollOptions()]);
      setPolls(nextPolls);
      setOptions(nextOptions);
    } catch {
      setError('Time polls could not be loaded right now. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => { void load(); }, [load, activeTenant?.tenant?.id]);

  const openDetail = async (poll: TenantTimePoll) => {
    try {
      setWorking(true);
      setError(null);
      const detail = await getScheduleTimePoll(poll.pollId);
      setSelected(detail);
      setView('detail');
    } catch {
      setError('That time poll could not be opened. Please try again.');
    } finally {
      setWorking(false);
    }
  };

  const save = async (payload: TenantTimePollSavePayload) => {
    try {
      setWorking(true);
      setError(null);
      const saved = selected?.canEdit
        ? await updateScheduleTimePoll(selected.pollId, payload)
        : await createScheduleTimePoll(payload);
      setSelected(saved);
      setPolls((current) => [saved, ...current.filter((poll) => poll.pollId !== saved.pollId)]);
      setView('detail');
    } catch {
      setError('The draft could not be saved. Review the details and try again.');
    } finally {
      setWorking(false);
    }
  };

  const changeStatus = async (action: 'open' | 'close') => {
    if (!selected) return;
    if (action === 'open' && !window.confirm('Ask the selected families now? The options and audience will be locked.')) return;
    try {
      setWorking(true);
      setError(null);
      const updated = action === 'open'
        ? await openScheduleTimePoll(selected.pollId)
        : await closeScheduleTimePoll(selected.pollId);
      setSelected(updated);
      setPolls((current) => current.map((poll) => poll.pollId === updated.pollId ? updated : poll));
    } catch {
      setError(action === 'open' ? 'Families could not be notified. Please try again.' : 'The poll could not be closed. Please try again.');
    } finally {
      setWorking(false);
    }
  };

  const buildSchedule = async () => {
    if (!selected || !scheduleDraftsEnabled) return;
    try {
      setWorking(true);
      setError(null);
      const draft = await generateScheduleDraft(selected.pollId);
      navigate(`/schedule/drafts/${draft.draftId}`);
    } catch {
      setError('The draft schedule could not be built. Review the poll and try again.');
    } finally {
      setWorking(false);
    }
  };

  if (tenantLoading || loading) {
    return <div className="flex min-h-64 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;
  }

  if (!enabled) {
    return (
      <Card variant="default" className="max-w-xl">
        <CardContent className="space-y-4 py-6">
          <p className="font-semibold text-text">Time polls are not enabled for this workspace.</p>
          <Button variant="outline" onClick={() => navigate('/schedule')}>Back to Schedule</Button>
        </CardContent>
      </Card>
    );
  }

  const back = () => {
    if (view === 'list') navigate('/schedule');
    else { setView('list'); setSelected(null); setError(null); }
  };

  return (
    <div className="w-full space-y-6 pb-24 md:pb-8">
      <CoachPageHeader
        eyebrow="Coach Studio"
        title="Time polls"
        subtitle="Ask selected families to rank possible times before you schedule a class."
        meta={<span className="rounded-full border border-border bg-surface px-3 py-1 text-xs font-semibold text-text-muted">{activeTenant?.tenant?.displayName}</span>}
        action={view === 'list' ? <Button leftIcon={<Plus />} onClick={() => { setSelected(null); setView('builder'); }}>New poll</Button> : null}
      />

      <Button variant="ghost" leftIcon={<ArrowLeft />} onClick={back}>{view === 'list' ? 'Schedule' : 'All polls'}</Button>
      {error ? <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-4 py-3 text-sm text-red-600">{error}</div> : null}

      {view === 'list' ? <CoachTimePollList polls={polls} onCreate={() => { setSelected(null); setView('builder'); }} onOpen={(poll) => void openDetail(poll)} /> : null}
      {view === 'builder' && options ? <CoachTimePollBuilder options={options} poll={selected} saving={working} onCancel={back} onSave={save} /> : null}
      {view === 'detail' && selected ? <CoachTimePollDetail poll={selected} working={working} onEdit={() => setView('builder')} onOpen={() => void changeStatus('open')} onClose={() => void changeStatus('close')} onBuildSchedule={scheduleDraftsEnabled ? () => void buildSchedule() : undefined} /> : null}

      {working && view === 'list' ? <div className="flex items-center gap-2 text-sm text-text-muted"><CalendarClock className="h-4 w-4" /> Opening poll...</div> : null}
    </div>
  );
}
